/**
 * RulesPage.jsx
 * View, edit, and delete dependency rules for the selected asset.
 * Also allows creating new rules via the existing AddRuleModal.
 */

import React, { useState, useMemo } from 'react';
import useStore from '../store/useStore';
import AddRuleModal from '../components/AddRuleModal';
import './RulesPage.css';

// ─── Constants ─────────────────────────────────────────────────────────────────

const RULE_TYPE_CONFIG = {
  Required:    { color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', label: 'Required' },
  Contributes: { color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC', label: 'Contributes' },
  Blocks:      { color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', label: 'Blocks' },
};

const ENTITY_TYPE_CONFIG = {
  Metric:     { color: '#2563EB', bg: '#EFF6FF', icon: '📈' },
  Project:    { color: '#7C3AED', bg: '#F5F3FF', icon: '🏗️' },
  CSF:        { color: '#D97706', bg: '#FFFBEB', icon: '⭐' },
  Constraint: { color: '#DC2626', bg: '#FEF2F2', icon: '🚧' },
  Initiative: { color: '#059669', bg: '#ECFDF5', icon: '🚀' },
};

const EXAMPLE_CONDITIONS = {
  Status: ["Status == 'Met'", "Status == 'Completed'", "Status != 'Blocked'"],
  Score:  ['NumericValue >= 0.8', 'NumericValue < 0.5', 'NumericValue >= 1.0'],
  Boolean: ['BooleanValue == true', 'BooleanValue == false'],
};

// ─── Entity badge helper ────────────────────────────────────────────────────────

function EntityBadge({ entity }) {
  if (!entity) return <span className="rp-entity-badge rp-entity-badge--unknown">Unknown</span>;
  const conf = ENTITY_TYPE_CONFIG[entity.EntityType] || {};
  return (
    <span
      className="rp-entity-badge"
      style={{ background: conf.bg || '#F3F4F6', color: conf.color || '#374151' }}
    >
      {conf.icon} {entity.Title}
      <span className="rp-entity-type">{entity.EntityType}</span>
    </span>
  );
}

// ─── Edit / Create modal ───────────────────────────────────────────────────────

function RuleFormModal({ existingRule, entities, onClose, onSubmit, isSubmitting }) {
  const isEdit = Boolean(existingRule);

  const [formData, setFormData] = useState(() => {
    if (existingRule) {
      return {
        Title:               existingRule.Title || '',
        SourceEntityId:      String(existingRule.SourceEntityIdLookupId || ''),
        TargetEntityId:      String(existingRule.TargetEntityIdLookupId || ''),
        RuleType:            existingRule.RuleType || 'Required',
        ConditionExpression: existingRule.ConditionExpression || '',
        ImpactValue:         existingRule.ImpactValue != null ? String(existingRule.ImpactValue) : '',
        ImpactUnit:          existingRule.ImpactUnit || 'Absolute',
        Priority:            existingRule.Priority ?? 10,
        IsActive:            existingRule.IsActive !== false,
        Description:         existingRule.Description || '',
      };
    }
    return {
      Title: '', SourceEntityId: '', TargetEntityId: '',
      RuleType: 'Required', ConditionExpression: '',
      ImpactValue: '', ImpactUnit: 'Absolute', Priority: 10,
      IsActive: true, Description: '',
    };
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));

    // Auto-generate title on new rule
    if (!isEdit && (name === 'SourceEntityId' || name === 'TargetEntityId' || name === 'RuleType')) {
      const srcId = name === 'SourceEntityId' ? value : formData.SourceEntityId;
      const tgtId = name === 'TargetEntityId' ? value : formData.TargetEntityId;
      const type2 = name === 'RuleType' ? value : formData.RuleType;
      const src = entities.find((e) => e.Id === parseInt(srcId));
      const tgt = entities.find((e) => e.Id === parseInt(tgtId));
      if (src && tgt && !formData.Title) {
        setFormData((prev) => ({ ...prev, Title: `${src.Title} ${type2.toLowerCase()} ${tgt.Title}` }));
      }
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.Title.trim())               errs.Title = 'Required';
    if (!formData.SourceEntityId)             errs.SourceEntityId = 'Required';
    if (!formData.TargetEntityId)             errs.TargetEntityId = 'Required';
    if (formData.SourceEntityId === formData.TargetEntityId) errs.TargetEntityId = 'Must differ from source';
    if (!formData.ConditionExpression.trim()) errs.ConditionExpression = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      Title:                    formData.Title.trim(),
      SourceEntityIdLookupId:   parseInt(formData.SourceEntityId),
      TargetEntityIdLookupId:   parseInt(formData.TargetEntityId),
      RuleType:                 formData.RuleType,
      ConditionExpression:      formData.ConditionExpression.trim(),
      ImpactValue:              formData.ImpactValue ? parseFloat(formData.ImpactValue) : null,
      ImpactUnit:               formData.ImpactUnit,
      Priority:                 parseInt(formData.Priority) || 10,
      IsActive:                 formData.IsActive,
      Description:              formData.Description.trim(),
    });
  };

  const sourceEntity = entities.find((e) => e.Id === parseInt(formData.SourceEntityId));
  const targetEntity = entities.find((e) => e.Id === parseInt(formData.TargetEntityId));
  const rtConf       = RULE_TYPE_CONFIG[formData.RuleType] || {};

  return (
    <div className="rp-modal-backdrop" onClick={onClose}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rp-modal__header">
          <h2>{isEdit ? 'Edit Rule' : 'New Dependency Rule'}</h2>
          <button className="rp-modal__close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="rp-form">
          {/* Title */}
          <div className="rp-field">
            <label>Rule Title *</label>
            <input
              name="Title"
              value={formData.Title}
              onChange={handleChange}
              placeholder="e.g., Production depends on Phase 4"
              className={errors.Title ? 'rp-input--error' : ''}
              disabled={isSubmitting}
            />
            {errors.Title && <span className="rp-error">{errors.Title}</span>}
          </div>

          {/* Source / Target */}
          <div className="rp-row">
            <div className="rp-field">
              <label>Source Entity (affects) *</label>
              <select
                name="SourceEntityId"
                value={formData.SourceEntityId}
                onChange={handleChange}
                className={errors.SourceEntityId ? 'rp-input--error' : ''}
                disabled={isSubmitting}
              >
                <option value="">— Select source —</option>
                {entities.map((ent) => (
                  <option key={ent.Id} value={ent.Id}>
                    [{ent.EntityType}] {ent.Title}
                  </option>
                ))}
              </select>
              {errors.SourceEntityId && <span className="rp-error">{errors.SourceEntityId}</span>}
            </div>

            <div className="rp-field">
              <label>Target Entity (affected) *</label>
              <select
                name="TargetEntityId"
                value={formData.TargetEntityId}
                onChange={handleChange}
                className={errors.TargetEntityId ? 'rp-input--error' : ''}
                disabled={isSubmitting}
              >
                <option value="">— Select target —</option>
                {entities.map((ent) => (
                  <option key={ent.Id} value={ent.Id}>
                    [{ent.EntityType}] {ent.Title}
                  </option>
                ))}
              </select>
              {errors.TargetEntityId && <span className="rp-error">{errors.TargetEntityId}</span>}
            </div>
          </div>

          {/* Rule preview */}
          {sourceEntity && targetEntity && (
            <div className="rp-preview" style={{ borderColor: rtConf.border, background: rtConf.bg }}>
              <span className="rp-preview__entity">{sourceEntity.Title}</span>
              <span className="rp-preview__arrow" style={{ color: rtConf.color }}>
                ─ {formData.RuleType} →
              </span>
              <span className="rp-preview__entity">{targetEntity.Title}</span>
            </div>
          )}

          {/* Rule type */}
          <div className="rp-field">
            <label>Rule Type *</label>
            <div className="rp-rule-type-group">
              {Object.entries(RULE_TYPE_CONFIG).map(([type, conf]) => (
                <label
                  key={type}
                  className={`rp-rule-type-option ${formData.RuleType === type ? 'selected' : ''}`}
                  style={formData.RuleType === type
                    ? { borderColor: conf.color, background: conf.bg }
                    : {}}
                >
                  <input
                    type="radio"
                    name="RuleType"
                    value={type}
                    checked={formData.RuleType === type}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <span className="rp-rule-type-label" style={{ color: formData.RuleType === type ? conf.color : undefined }}>
                    {type}
                  </span>
                  <span className="rp-rule-type-desc">
                    {type === 'Required'    && 'Target is blocked if unmet'}
                    {type === 'Contributes' && 'Adds value when met'}
                    {type === 'Blocks'      && 'Reduces value when true'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Condition */}
          <div className="rp-field">
            <label>Condition Expression *</label>
            <input
              name="ConditionExpression"
              value={formData.ConditionExpression}
              onChange={handleChange}
              placeholder="e.g., Status == 'Met'"
              className={errors.ConditionExpression ? 'rp-input--error' : ''}
              disabled={isSubmitting}
            />
            {errors.ConditionExpression && <span className="rp-error">{errors.ConditionExpression}</span>}
            <div className="rp-examples">
              {Object.entries(EXAMPLE_CONDITIONS).map(([cat, exps]) => (
                <div key={cat} className="rp-examples__cat">
                  <span className="rp-examples__cat-label">{cat}:</span>
                  {exps.map((exp) => (
                    <code
                      key={exp}
                      className="rp-example-code"
                      onClick={() => setFormData((prev) => ({ ...prev, ConditionExpression: exp }))}
                      title="Click to use"
                    >
                      {exp}
                    </code>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Impact + Priority */}
          <div className="rp-row">
            <div className="rp-field">
              <label>Impact Value</label>
              <input
                type="number"
                name="ImpactValue"
                value={formData.ImpactValue}
                onChange={handleChange}
                step="0.01"
                placeholder="e.g., 3700"
                disabled={isSubmitting}
              />
            </div>
            <div className="rp-field">
              <label>Impact Unit</label>
              <select name="ImpactUnit" value={formData.ImpactUnit} onChange={handleChange} disabled={isSubmitting}>
                <option value="Absolute">Absolute (fixed number)</option>
                <option value="Percentage">Percentage (% of target)</option>
              </select>
            </div>
            <div className="rp-field">
              <label>Priority (1–100)</label>
              <input
                type="number"
                name="Priority"
                value={formData.Priority}
                onChange={handleChange}
                min="1"
                max="100"
                disabled={isSubmitting}
              />
              <small className="rp-hint">
                1–49 = critical (red when unmet) · 50–100 = minor (amber)
              </small>
            </div>
          </div>

          {/* Active toggle + Description */}
          <div className="rp-row">
            <div className="rp-field rp-field--toggle">
              <label className="rp-toggle">
                <input
                  type="checkbox"
                  name="IsActive"
                  checked={formData.IsActive}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <span className="rp-toggle__track" />
                <span className="rp-toggle__label">
                  {formData.IsActive ? 'Active' : 'Inactive'}
                </span>
              </label>
            </div>
            <div className="rp-field" style={{ flex: 2 }}>
              <label>Description</label>
              <textarea
                name="Description"
                value={formData.Description}
                onChange={handleChange}
                rows="2"
                placeholder="Optional notes"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="rp-form__actions">
            <button type="button" className="rp-btn rp-btn--cancel" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="rp-btn rp-btn--primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Rule card ─────────────────────────────────────────────────────────────────

function RuleCard({ rule, entities, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const srcEntity = entities.find((e) => e.Id === rule.SourceEntityIdLookupId);
  const tgtEntity = entities.find((e) => e.Id === rule.TargetEntityIdLookupId);
  const rtConf    = RULE_TYPE_CONFIG[rule.RuleType] || {};
  const priority  = rule.Priority ?? 10;
  const isCritical = priority < 50;

  return (
    <div className={`rule-card ${!rule.IsActive ? 'rule-card--inactive' : ''}`}>
      {/* Card header */}
      <div className="rule-card__header">
        <div className="rule-card__title-row">
          <span
            className="rule-card__type"
            style={{ background: rtConf.bg, color: rtConf.color, borderColor: rtConf.border }}
          >
            {rule.RuleType}
          </span>
          <h3 className="rule-card__title">{rule.Title}</h3>
          {!rule.IsActive && <span className="rule-card__inactive-badge">Inactive</span>}
        </div>

        <div className="rule-card__actions">
          <span className={`priority-badge priority-badge--${isCritical ? 'critical' : 'minor'}`}>
            P{priority}
          </span>
          {!confirmDelete ? (
            <>
              <button className="rp-icon-btn rp-icon-btn--edit" onClick={() => onEdit(rule)} title="Edit rule">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M14.7 2.3a1 1 0 011.4 1.4L5.4 14.4 3 15l.6-2.4L14.7 2.3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13 4l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Edit
              </button>
              <button className="rp-icon-btn rp-icon-btn--delete" onClick={() => setConfirmDelete(true)} title="Delete rule">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M3 6h14M8 6V4h4v2M5 6l1 12h8l1-12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Delete
              </button>
            </>
          ) : (
            <div className="rp-confirm-delete">
              <span>Delete this rule?</span>
              <button className="rp-btn rp-btn--danger-sm" onClick={() => onDelete(rule.Id)}>
                Yes, delete
              </button>
              <button className="rp-btn rp-btn--cancel-sm" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Entity flow */}
      <div className="rule-card__flow">
        <EntityBadge entity={srcEntity} />
        <div className="rule-card__flow-arrow" style={{ color: rtConf.color }}>
          <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
            <path d="M0 5h18M14 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="rule-card__flow-label">{rule.RuleType}</span>
        </div>
        <EntityBadge entity={tgtEntity} />
      </div>

      {/* Condition + impact */}
      <div className="rule-card__details">
        {rule.ConditionExpression && (
          <div className="rule-card__detail">
            <span className="rule-card__detail-label">Condition</span>
            <code className="rule-card__condition">{rule.ConditionExpression}</code>
          </div>
        )}
        {rule.ImpactValue != null && (
          <div className="rule-card__detail">
            <span className="rule-card__detail-label">Impact</span>
            <span className="rule-card__detail-value">
              {rule.ImpactValue.toLocaleString()}
              {rule.ImpactUnit === 'Percentage' ? '%' : ''}
              &nbsp;({rule.ImpactUnit})
            </span>
          </div>
        )}
        {rule.Description && (
          <div className="rule-card__detail">
            <span className="rule-card__detail-label">Note</span>
            <span className="rule-card__detail-value rule-card__detail-value--muted">{rule.Description}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

const RulesPage = () => {
  const { rules, entities, selectedAsset, updateRule, deleteRule, isLoading } = useStore();

  const [typeFilter, setTypeFilter] = useState('All');
  const [showAddModal, setShowAddModal]   = useState(false);
  const [editingRule, setEditingRule]     = useState(null);
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [deletingId, setDeletingId]       = useState(null);

  // Filter & sort rules
  const filtered = useMemo(() => {
    let list = [...(rules || [])];
    if (typeFilter !== 'All') list = list.filter((r) => r.RuleType === typeFilter);
    return list.sort((a, b) => (a.Priority ?? 10) - (b.Priority ?? 10));
  }, [rules, typeFilter]);

  // Counts per type
  const counts = useMemo(() => {
    const c = { All: rules?.length || 0, Required: 0, Contributes: 0, Blocks: 0 };
    (rules || []).forEach((r) => { if (c[r.RuleType] !== undefined) c[r.RuleType]++; });
    return c;
  }, [rules]);

  const handleEdit = (rule) => setEditingRule(rule);

  const handleEditSubmit = async (ruleData) => {
    setIsSubmitting(true);
    try {
      await updateRule(editingRule.Id, ruleData);
      setEditingRule(null);
    } catch {
      alert('Failed to update rule. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (ruleId) => {
    setDeletingId(ruleId);
    try {
      await deleteRule(ruleId);
    } catch {
      alert('Failed to delete rule. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!selectedAsset) {
    return (
      <div className="rp-empty-state">
        <p>No asset selected. Please select an asset to manage its rules.</p>
      </div>
    );
  }

  return (
    <div className="rules-page">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="rp-header">
        <div className="rp-header__left">
          <h2 className="rp-header__title">Dependency Rules</h2>
          <p className="rp-header__subtitle">
            {selectedAsset.Title} · {counts.All} rule{counts.All !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="rp-btn rp-btn--primary" onClick={() => setShowAddModal(true)}>
          + Add Rule
        </button>
      </div>

      {/* ── Priority legend ─────────────────────────────────────────────── */}
      <div className="rp-legend">
        <span className="rp-legend__item">
          <span className="priority-badge priority-badge--critical">P1–49</span>
          Critical — entity turns <strong>RED</strong> if unmet
        </span>
        <span className="rp-legend__item">
          <span className="priority-badge priority-badge--minor">P50–100</span>
          Minor — entity turns <strong>AMBER</strong> if unmet
        </span>
      </div>

      {/* ── Type filter tabs ────────────────────────────────────────────── */}
      <div className="rp-tabs">
        {['All', 'Required', 'Contributes', 'Blocks'].map((type) => {
          const conf = RULE_TYPE_CONFIG[type] || {};
          return (
            <button
              key={type}
              className={`rp-tab ${typeFilter === type ? 'rp-tab--active' : ''}`}
              style={typeFilter === type && type !== 'All'
                ? { borderColor: conf.color, color: conf.color, background: conf.bg }
                : {}}
              onClick={() => setTypeFilter(type)}
            >
              {type}
              <span className="rp-tab__count">{counts[type] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* ── Rules list ──────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="rp-empty-state">
          {(rules?.length || 0) === 0 ? (
            <>
              <div className="rp-empty-icon">🔗</div>
              <p>No rules defined for this asset yet.</p>
              <button className="rp-btn rp-btn--primary" onClick={() => setShowAddModal(true)}>
                Create your first rule
              </button>
            </>
          ) : (
            <p>No {typeFilter} rules found.</p>
          )}
        </div>
      ) : (
        <div className="rp-rules-list">
          {filtered.map((rule) => (
            <RuleCard
              key={rule.Id}
              rule={rule}
              entities={entities}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* ── Edit modal ──────────────────────────────────────────────────── */}
      {editingRule && (
        <RuleFormModal
          existingRule={editingRule}
          entities={entities}
          onClose={() => setEditingRule(null)}
          onSubmit={handleEditSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {/* ── Add modal (existing component) ──────────────────────────────── */}
      {showAddModal && (
        <AddRuleModal onClose={() => setShowAddModal(false)} />
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="rp-loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}
    </div>
  );
};

export default RulesPage;
